import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDateAU } from '../../../shared/format';
import { RULES, RULE_KEYS, DEFAULT_WINDOWS, windowsInEffect, type ComplianceRuleVersion, type ComplianceWindows, type RuleKey } from '../../../shared/compliance';
import { windowLabel } from '../../../shared/bookEdit';
import type { PracticeSettings } from '../../../shared/practiceSettings';

const NAMES: Record<RuleKey, string> = Object.fromEntries(RULE_KEYS.map((k) => [k, RULES[k].name])) as Record<RuleKey, string>;

/**
 * Compliance windows as dated versions. The version in force today is shown;
 * a change is scheduled as a new version from an effective date, and versions
 * already in force are locked (the server refuses to edit them).
 */
export function ComplianceRulesPanel({ settings, today, canEdit }: { settings: PracticeSettings; today: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const versions = settings.compliance.versions;
  const current = windowsInEffect(versions, today);
  const currentVersion = [...versions].reverse().find((v) => v.effectiveFrom <= today) ?? null;
  const future = versions.filter((v) => v.effectiveFrom > today);
  const [draft, setDraft] = useState<ComplianceWindows | null>(null);
  const [from, setFrom] = useState(today);
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const w = draft ?? current;
  const changed = RULE_KEYS.filter((k) => w[k] !== current[k]);

  async function put(next: ComplianceRuleVersion[], done: string) {
    setBusy(true); setStatus(null);
    try {
      await api('/api/settings', { method: 'PUT', body: JSON.stringify({ compliance: { versions: next } }) });
      await Promise.all(['settings', 'today', 'dashboard', 'client'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      setDraft(null); setNote(''); setFrom(today);
      setStatus(done);
    } catch (e) { setStatus(e instanceof Error ? e.message : 'Save failed'); } finally { setBusy(false); }
  }

  function schedule() {
    if (!changed.length) { setStatus('Nothing has changed.'); return; }
    if (!from || from < today) { setStatus('The effective date cannot be in the past.'); return; }
    if (versions.some((v) => v.effectiveFrom === from)) { setStatus('A version already takes effect that day.'); return; }
    const v: ComplianceRuleVersion = { effectiveFrom: from, windows: w, ...(note.trim() ? { note: note.trim() } : {}) };
    void put([...versions, v].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom)),
      from === today ? 'New rules in force from today.' : `New rules scheduled from ${formatDateAU(from)}.`);
  }

  return (
    <section className="qa-card p-5 mb-6" aria-label="Compliance rules" data-testid="compliance-rules">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <p className="qa-eyebrow">Compliance rules</p>
        <p className="text-xs text-ink-mute">
          {currentVersion ? `In force since ${formatDateAU(currentVersion.effectiveFrom)}${currentVersion.changedBy ? `, set by ${currentVersion.changedBy}` : ''}` : 'Built-in defaults in force'}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="qa-table text-sm">
          <thead>
            <tr><th>Check</th><th className="text-right">Window in force</th>{canEdit && <th className="text-right">New window (days)</th>}<th>Default</th></tr>
          </thead>
          <tbody>
            {RULE_KEYS.map((k) => (
              <tr key={k}>
                <td>{NAMES[k]}</td>
                <td className="num">{windowLabel(current[k])}</td>
                {canEdit && (
                  <td className="text-right">
                    <input type="number" min={1} max={3650} className={`qa-input max-w-[7rem] ml-auto text-right qa-mono ${w[k] !== current[k] ? 'border-rust' : ''}`}
                      aria-label={`${NAMES[k]} window in days`} value={w[k]}
                      onChange={(e) => setDraft({ ...w, [k]: Math.max(1, Math.min(3650, Math.round(Number(e.target.value) || 1))) })} />
                    {w[k] !== current[k] && <span className="block text-[11px] text-rust mt-0.5">{windowLabel(w[k])}</span>}
                  </td>
                )}
                <td className="text-xs text-ink-mute">{windowLabel(DEFAULT_WINDOWS[k])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div className="flex flex-wrap items-end gap-3 mt-4 pt-3 border-t border-hairline-paper">
          <label className="text-xs">Effective from
            <input type="date" className="qa-input block mt-1 text-sm" min={today} value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Effective from" />
          </label>
          <label className="text-xs flex-1 min-w-[14rem]">Reason (shown in the history)
            <input className="qa-input block w-full mt-1 text-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Licensee policy update, October 2026" aria-label="Reason" />
          </label>
          <button type="button" className="btn-primary text-sm" disabled={busy || !changed.length} onClick={schedule}>
            {from === today ? 'Apply from today' : 'Schedule change'}
          </button>
          {draft && <button type="button" className="btn-ghost text-xs" onClick={() => setDraft(null)}>Discard</button>}
          <p className="basis-full text-[11px] text-ink-mute">Once a version is in force it is locked, so the record shows which rule applied on any day. Scheduled changes can be cancelled until their date.</p>
        </div>
      )}
      {status && <p role="status" className="text-xs text-ink-soft mt-2">{status}</p>}

      {versions.length > 0 && (
        <details className="mt-4">
          <summary className="text-xs text-ink-mute cursor-pointer">History ({versions.length} version{versions.length === 1 ? '' : 's'})</summary>
          <ul className="mt-2 space-y-2 text-xs">
            {[...versions].reverse().map((v) => {
              const diffs = RULE_KEYS.filter((k) => v.windows[k] !== DEFAULT_WINDOWS[k]);
              const pending = v.effectiveFrom > today;
              return (
                <li key={v.effectiveFrom} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-hairline-paper pb-2">
                  <span className="qa-mono font-semibold">{formatDateAU(v.effectiveFrom)}</span>
                  <span className={pending ? 'text-brass-deep' : v === currentVersion ? 'text-forest' : 'text-ink-mute'}>
                    {pending ? 'Scheduled' : v === currentVersion ? 'In force' : 'Superseded'}
                  </span>
                  <span className="text-ink-soft">{diffs.length ? diffs.map((k) => `${NAMES[k]} ${windowLabel(v.windows[k])}`).join(' · ') : 'Defaults'}</span>
                  {v.note && <span className="text-ink-mute italic">{v.note}</span>}
                  {v.changedBy && <span className="text-ink-mute">by {v.changedBy}{v.changedAt ? ` on ${formatDateAU(v.changedAt)}` : ''}</span>}
                  {pending && canEdit && (
                    <button type="button" className="ml-auto text-rust hover:underline" disabled={busy}
                      onClick={() => put(versions.filter((x) => x !== v), `Scheduled change for ${formatDateAU(v.effectiveFrom)} removed.`)}>
                      Cancel
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}
      {future.length > 0 && <p className="text-[11px] text-brass-deep mt-2">{future.length} change{future.length === 1 ? '' : 's'} scheduled.</p>}
    </section>
  );
}
